/**
 * Loads all exhibition metaobjects via Storefront API for current/upcoming sections.
 * Liquid is capped at 50 entries per metaobject type; this bypasses that limit.
 */
(function () {
  'use strict';

  var lists = document.querySelectorAll('[data-esc-exhibition-list]');
  if (!lists.length) return;

  var config = window.theme && window.theme.exhibitionSearch;

  function activateFallback(listEl) {
    var container = listEl.closest('.events__list-container');
    if (!container) return;

    var target = listEl.getAttribute('data-target-status');
    var fallbackList = container.querySelector(
      '[data-esc-exhibition-fallback-list][data-target-status="' + target + '"]'
    );
    if (!fallbackList || !fallbackList.children.length) return;

    listEl.innerHTML = fallbackList.innerHTML;
    initImages(listEl);

    var section = listEl.closest('[data-section-id]');
    if (section && target === 'current' && section.getAttribute('data-show-tabs') === 'true') {
      var groups = [];
      listEl.querySelectorAll('.event-item[data-tab-group]').forEach(function (item) {
        var g = item.getAttribute('data-tab-group');
        if (g && groups.indexOf(g) === -1) groups.push(g);
      });
      if (groups.length) {
        buildTabs(section, groups, section.getAttribute('data-show-all-tab') !== 'false');
      }
    }

    if (section) refreshCarousel(section);
    if (section && target === 'current') initTabFiltering(section);
  }

  if (!config || !config.apiUrl || !config.token) {
    lists.forEach(activateFallback);
    return;
  }

  var CACHE_KEY = 'maddox-exhibitions-listings-cache-v2';
  var CACHE_TTL = config.cacheTtlMs || 300000;
  var cache = null;
  var loading = null;

  var QUERY = [
    'query ExhibitionListings($type: String!, $first: Int!, $after: String) {',
    '  metaobjects(type: $type, first: $first, after: $after) {',
    '    pageInfo { hasNextPage endCursor }',
    '    edges {',
    '      node {',
    '        handle',
    '        onlineStoreUrl',
    '        title: field(key: "title") { value }',
    '        location: field(key: "location") { value }',
    '        date_display: field(key: "date_display") { value }',
    '        exhibition_card_summary_text: field(key: "exhibition_card_summary_text") { value }',
    '        status: field(key: "status") { value }',
    '        start_date: field(key: "start_date") { value }',
    '        end_date: field(key: "end_date") { value }',
    '        current_exhibition_tab_group: field(key: "current_exhibition_tab_group") { value }',
    '        hero_image: field(key: "hero_image") {',
    '          reference {',
    '            ... on MediaImage {',
    '              image { url(transform: { maxWidth: 800 }) altText }',
    '            }',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  function fieldValue(field) {
    if (!field || field.value == null) return '';
    return String(field.value).trim();
  }

  function parseTs(value) {
    if (!value) return 0;
    var ts = Date.parse(value);
    return isNaN(ts) ? 0 : ts;
  }

  function resolveStatus(ex) {
    var status = (ex.status || '').toLowerCase();
    if (status) return status;
    var now = Date.now();
    var startTs = parseTs(ex.start_date);
    var endTs = parseTs(ex.end_date);
    if (startTs && now < startTs) return 'upcoming';
    if (endTs && now > endTs) return 'past';
    if (startTs || endTs) return 'current';
    return 'unknown';
  }

  function formatDate(value) {
    if (!value) return '';
    var d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function normalize(node) {
    var hero = node.hero_image && node.hero_image.reference && node.hero_image.reference.image;
    var url = node.onlineStoreUrl || '';
    if (!url && node.handle) url = '/pages/exhibition/' + node.handle;

    return {
      handle: node.handle || '',
      title: fieldValue(node.title),
      url: url,
      location: fieldValue(node.location),
      date_display: fieldValue(node.date_display),
      exhibition_card_summary_text: fieldValue(node.exhibition_card_summary_text),
      status: fieldValue(node.status),
      start_date: fieldValue(node.start_date),
      end_date: fieldValue(node.end_date),
      tab_group: fieldValue(node.current_exhibition_tab_group),
      hero_image_url: hero && hero.url ? hero.url : '',
      hero_image_alt: hero && hero.altText ? hero.altText : fieldValue(node.title),
    };
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.at || Date.now() - parsed.at > CACHE_TTL) return null;
      return parsed.list || null;
    } catch (e) {
      return null;
    }
  }

  function writeCache(list) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), list: list }));
    } catch (e) {
      /* ignore */
    }
  }

  function apiFetch(variables) {
    return fetch(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.token,
      },
      body: JSON.stringify({ query: QUERY, variables: variables }),
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) throw new Error((json && json.message) || 'HTTP ' + res.status);
        if (json.errors && json.errors.length) {
          throw new Error(json.errors.map(function (e) { return e.message; }).join('; '));
        }
        return json;
      });
    });
  }

  function loadAll() {
    if (cache) return Promise.resolve(cache);
    if (loading) return loading;

    var cached = readCache();
    if (cached) {
      cache = cached;
      return Promise.resolve(cache);
    }

    loading = (function fetchPage(after, acc) {
      return apiFetch({
        type: config.metaobjectType || 'exhibition',
        first: 100,
        after: after || null,
      }).then(function (json) {
        var mo = json.data && json.data.metaobjects;
        var edges = (mo && mo.edges) || [];
        edges.forEach(function (edge) {
          if (edge && edge.node) acc.push(normalize(edge.node));
        });
        if (mo && mo.pageInfo && mo.pageInfo.hasNextPage) {
          return fetchPage(mo.pageInfo.endCursor, acc);
        }
        return acc;
      });
    })(null, [])
      .then(function (list) {
        cache = list;
        writeCache(list);
        return list;
      })
      .catch(function () {
        loading = null;
        return Promise.reject(new Error('exhibition listings fetch failed'));
      });

    return loading;
  }

  function dateLine(ex) {
    if (ex.date_display) return ex.date_display;
    var start = formatDate(ex.start_date);
    var end = formatDate(ex.end_date);
    if (start && end) return start + ' \u2014 ' + end;
    return start || end || '';
  }

  function buildCard(ex, opts) {
    var carousel = opts.carousel;
    var showSummary = opts.showSummary !== false;
    var liClass = 'event-item' + (ex.hero_image_url ? ' event-item--has-image' : '');
    if (carousel) liClass += ' carousel__slide swiper-slide';

    var html = '<li class="' + liClass + '"';
    if (ex.tab_group) html += ' data-tab-group="' + escapeHtml(ex.tab_group) + '"';
    html += '><a href="' + escapeHtml(ex.url) + '" class="event-item__link">';
    html += '<div class="event-item__image-wrapper">';
    if (ex.hero_image_url) {
      html += '<div class="image event-item__image aspect-ratio--square image--animate animation--lazy-load">';
      html += '<img src="' + escapeHtml(ex.hero_image_url) + '" alt="' + escapeHtml(ex.hero_image_alt) + '" loading="lazy" class="image__img">';
      html += '</div>';
    }
    html += '</div><div class="event-item__details">';
    if (ex.title) {
      html += '<h3 class="event-item__name ff-heading fs-heading-5-base">' + escapeHtml(ex.title) + '</h3>';
    }
    if (showSummary && ex.exhibition_card_summary_text) {
      html += '<p class="event-item__summary fs-body-100">' + escapeHtml(ex.exhibition_card_summary_text) + '</p>';
    }
    var dates = dateLine(ex);
    if (dates) html += '<p class="event-item__date fs-body-75">' + escapeHtml(dates) + '</p>';
    if (ex.location) html += '<p class="event-item__venue fs-body-75">' + escapeHtml(ex.location) + '</p>';
    html += '</div></a></li>';
    return html;
  }

  function initImages(container) {
    container.querySelectorAll('.animation--lazy-load img.image__img').forEach(function (img) {
      function markLoaded() {
        var wrap = img.closest('.animation--lazy-load');
        if (wrap) wrap.classList.add('loaded');
      }
      if (img.complete) markLoaded();
      else {
        img.addEventListener('load', markLoaded);
        img.addEventListener('error', markLoaded);
      }
    });
  }

  function refreshCarousel(section) {
    var swiperEl = section.querySelector('.carousel.swiper');
    if (swiperEl && swiperEl.swiper && typeof swiperEl.swiper.update === 'function') {
      swiperEl.swiper.update();
    }
  }

  function buildTabs(section, groups, showAllTab) {
    var nav = section.querySelector('[data-esc-exhibition-tab-nav]');
    if (!nav) return;
    var list = nav.querySelector('.events__tab-list');
    if (!list) return;

    var html = '';
    if (showAllTab !== false) {
      html += '<li><button class="events__tab selected" data-events-tab="all" type="button" aria-current="true">';
      html += '<span class="ff-heading fs-body-150">All</span><span class="indicator"></span></button></li>';
    }
    groups.forEach(function (group, i) {
      var selected = showAllTab === false && i === 0;
      html += '<li><button class="events__tab' + (selected ? ' selected' : '') + '" data-events-tab="' + escapeHtml(group) + '" type="button"';
      if (selected) html += ' aria-current="true"';
      html += '><span class="ff-heading fs-body-150">' + escapeHtml(group) + '</span><span class="indicator"></span></button></li>';
    });
    list.innerHTML = html;
    nav.hidden = false;
  }

  function initTabFiltering(section) {
    var tabNav = section.querySelector('[data-events-tab-nav]');
    if (!tabNav || tabNav.getAttribute('data-tabs-wired') === 'true') return;
    tabNav.setAttribute('data-tabs-wired', 'true');

    function applyTab(active) {
      tabNav.querySelectorAll('[data-events-tab]').forEach(function (t) {
        var v = t.getAttribute('data-events-tab');
        if (v === active) {
          t.classList.add('selected');
          t.setAttribute('aria-current', 'true');
        } else {
          t.classList.remove('selected');
          t.removeAttribute('aria-current');
        }
      });
      section.querySelectorAll('.event-item').forEach(function (item) {
        var group = item.getAttribute('data-tab-group') || '';
        if (active === 'all' || group === active) {
          item.classList.remove('event-item--hidden');
        } else {
          item.classList.add('event-item--hidden');
        }
      });
    }

    tabNav.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-events-tab]');
      if (!tab || !tabNav.contains(tab)) return;
      applyTab(tab.getAttribute('data-events-tab'));
    });

    var selected = tabNav.querySelector('.events__tab.selected');
    if (selected) applyTab(selected.getAttribute('data-events-tab'));
  }

  function renderList(listEl, exhibitions) {
    var target = listEl.getAttribute('data-target-status');
    var carousel = listEl.getAttribute('data-carousel') === 'true';
    var showSummary = listEl.getAttribute('data-show-card-summary') !== 'false';
    var designMode = listEl.getAttribute('data-design-mode') === 'true';
    var emptyMessage = listEl.getAttribute('data-empty-message') || '';
    var section = listEl.closest('[data-section-id]');

    var matched = exhibitions.filter(function (ex) {
      return resolveStatus(ex) === target;
    });

    if (section && section.getAttribute('data-show-tabs') === 'true' && target === 'current') {
      var groups = [];
      matched.forEach(function (ex) {
        if (ex.tab_group && groups.indexOf(ex.tab_group) === -1) groups.push(ex.tab_group);
      });
      var showAllTab = section.getAttribute('data-show-all-tab') !== 'false';
      buildTabs(section, groups, showAllTab);
    }

    if (!matched.length) {
      if (designMode && emptyMessage) {
        listEl.innerHTML = '<li style="padding:24px 0;opacity:0.5;list-style:none;">' + emptyMessage + '</li>';
      }
      return;
    }

    listEl.innerHTML = matched.map(function (ex) {
      return buildCard(ex, { carousel: carousel, showSummary: showSummary });
    }).join('');

    initImages(listEl);

    if (section) refreshCarousel(section);
    if (section && target === 'current') initTabFiltering(section);
  }

  loadAll()
    .then(function (exhibitions) {
      lists.forEach(function (listEl) {
        renderList(listEl, exhibitions);
      });
    })
    .catch(function () {
      lists.forEach(activateFallback);
    });
})();
